import { Component, Input, OnInit, OnDestroy, Output, EventEmitter } from "@angular/core";
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from "@angular/forms";
import { DataProvider } from "projects/packcert-web/src/app/providers/data.provider";
import { SpinnerService, Message, UtilsService } from 'shared-components';
import { GestionService } from "../../../services/gestion.service";
import { SessionContextService } from "projects/packcert-web/src/app/context/session-context.service";
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-cya-new-edit',
    templateUrl: './new-edit.component.html'
})
export class CyaNewEditComponent implements OnInit, OnDestroy {

    private readonly destroy$ = new Subject<void>();
    public _filters;
    private _campanya;
    public newEditForm: UntypedFormGroup;
    public showError: boolean;
    public showApiError: boolean; // evitar mostrar la clase de error en inputs si falla el getById
    public mensajes = new Array<Message>();
    public denominaciones;
    public disabled: Subject<boolean> = new Subject();

    @Input() campanyaId;
    @Output() refresh = new EventEmitter<boolean>();
    @Output() mensaje = new EventEmitter<string>();

    constructor(
        private _fb: UntypedFormBuilder,
        private readonly utilsService: UtilsService,
        private readonly _spinnerService: SpinnerService,
        private readonly _gestionService: GestionService,
        private readonly _dataProvider: DataProvider,
        private readonly _sessionContext: SessionContextService
    ) {}

    ngOnInit() {
        this.newEditForm = this._fb.group({
            pais: ['', Validators.required],
            denominacionId: [{ value: '', disabled: true }, Validators.required],
            fechaInicio: ['', Validators.required],
            fechaFin: ['', Validators.required],
            habilitada: [true, Validators.required],
            duplicarCultivosDeCampanyaAnterior: [true, Validators.required],
            duplicarCultivosMarca: [true, Validators.required],
            anyo: ['', Validators.required]
        });

        if(this.campanyaId) {
          this.newEditForm.addControl('des', new UntypedFormControl('', Validators.required));
        }

        this.newEditForm.get('pais').valueChanges.pipe(takeUntil(this.destroy$)).subscribe((pais) => {
            if (pais) {
                this._filters = {
                    paisId: pais.id
                };
                this._spinnerService.showSpinner();
                this._gestionService.getDenominacionesPais(this._filters).toPromise().finally(() => {
                    this._spinnerService.hideSpinner();
                }).then((resp) => {
                    if (resp.lista.length > 0) {
                        this.denominaciones = resp.lista;
                        if (this.campanyaId) {
                            this.newEditForm.get('denominacionId').patchValue(this.denominaciones.find(x => x.id === this._campanya.denominacionId))
                        }
                        this.disabled.next(false);
                        this.newEditForm.get('denominacionId').enable();
                    } else {
                        this.disabled.next(true);
                        this.newEditForm.get('denominacionId').disable();
                    }
                });
            } else {
                this.denominaciones = [];
                this.disabled.next(true);
                this.newEditForm.get('denominacionId').disable();
                this.newEditForm.get('denominacionId').patchValue('');
            }
        });

        if (this.campanyaId) {
            this.getCampanya();
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    public get campaigns() {
        return this._dataProvider.campaigns;
    }

    public get paises() {
        if (this._dataProvider.paises) {
            if (this._dataProvider.paises.length === 1 && this.newEditForm.get('pais').enabled) {
              this.newEditForm.get('pais').patchValue(this._dataProvider.paises[0]);
              this.newEditForm.get('pais').disable();
            }
          }
        return this._dataProvider.paises;
    }

    private handleApiError(error: any) {
        this._spinnerService.hideSpinner();
        this.showApiError = true;
        if (error.status === 420) {
            this.mensajes.push({ class: 'error', message: 'global.error.noAccess' });
        } else {
            this.mensajes.push({ class: 'error', message: 'global.error.noResponse' });
        }
    }

    private getCampanya() {
        this._spinnerService.showSpinner();
        this._gestionService.getCampanya(this.campanyaId).subscribe((resp) => {
            this._campanya = resp;
            this._gestionService.getDenominacionById(resp.denominacionId).subscribe((deno) => {
                this._spinnerService.hideSpinner();
                this.newEditForm.patchValue({
                    des: resp.des,
                    pais: this.paises.find(x => x.id === resp.paisId),
                    fechaInicio: new Date(resp.fechaInicio),
                    fechaFin: new Date(resp.fechaFin),
                    habilitada: resp.habilitada,
                    anyo: resp.anyo
                });
                this.disabled.next(false);
                this.newEditForm.get('denominacionId').enable();
            }, (error) => {
                this.handleApiError(error);
            });
        }, (error) => {
            this.handleApiError(error);
        });
    }

    public close() {
        this.refresh.emit(false);
    }

    public save() {
        this.checkForm();
        if (this.newEditForm.valid && !this.campanyaId) {
            this._campanya = {
                paisId: this.newEditForm.get('pais').value.id,
                lsDenominacionId: this.newEditForm.get('denominacionId').value,
                fechaInicio: this.utilsService.parseGMTtoYYYYMMDD(this.newEditForm.get('fechaInicio').value),
                fechaFin: this.utilsService.parseGMTtoYYYYMMDD(this.newEditForm.get('fechaFin').value),
                habilitada: this.newEditForm.get('habilitada').value,
                duplicarCultivosDeCampanyaAnterior: this.newEditForm.get('duplicarCultivosDeCampanyaAnterior').value,
                duplicarCultivosMarca: this.newEditForm.get('duplicarCultivosMarca').value,
                anyo: this.newEditForm.get('anyo').value,
                creaDesignacionesFincaDePruebas: true
            };
            this._spinnerService.showSpinner();
            this._gestionService.insertCampanyaMultiple(this._campanya).toPromise().finally(() => {
                this._spinnerService.hideSpinner();
            }).then((resp) => {
              this.refresh.emit(true);
              this.mensaje.emit('global.success.itemCreated');
            }, (err) => {
              this.showError = true;
              this.mensajes.push({ class: 'error', message: 'global.error.noResponse'});
            });
        } else if (this.newEditForm.valid && this.campanyaId) {
            this._campanya = {
                id: this.campanyaId,
                des: this.newEditForm.get('des').value,
                paisId: this.newEditForm.get('pais').value.id,
                denominacionId: this.newEditForm.get('denominacionId').value.id,
                fechaInicio: this.utilsService.parseGMTtoYYYYMMDD(this.newEditForm.get('fechaInicio').value),
                fechaFin: this.utilsService.parseGMTtoYYYYMMDD(this.newEditForm.get('fechaFin').value),
                habilitada: this.newEditForm.get('habilitada').value,
                anyo: this.newEditForm.get('anyo').value,
            }
            this._spinnerService.showSpinner();
            this._gestionService.updateCampanya(this._campanya).toPromise().finally(() => {
                this._spinnerService.hideSpinner();
            }).then((resp) => {
                    this.refresh.emit(true);
                    this.mensaje.emit('global.success.itemUpdated');
                },
                (error) => {
                    this.showError = true;
                    this.mensajes.push({ class: 'error', message: 'global.error.noResponse' });
                }
            );
        }
    }

    private checkForm() {
        this.showError = this.newEditForm.invalid;
        if (this.showError) {
            this.mensajes.push({ class: 'error', message: 'global.error.required' })
        }
    }

    shareCheckedListDenominaciones(item: any[]) {
      this.newEditForm.get('denominacionId').patchValue(item);
    }

}
